import MainHeading from "@/components/common/MainHeading"
import Layout from "@/components/layout/Layout"
import { LeaveService } from "@/services/Leave/LeaveSetting"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import type { TableColumn } from "@/components/common/CommonTable"
import { Button } from "@/components/ui/button"
import { RoutesLink } from "@/utility/RouteLinks"
import { Pencil, Plus, Trash, TriangleAlert } from "lucide-react"
import CommonTable from "@/components/common/CommonTable"
import CommonModal from "@/components/common/CommonModal"
import type { ILeaveSetting } from "@/interface/leave"
import moment from "moment"

const LeaveSettings: React.FC = () => {
    const navigate = useNavigate()
    const [leaveSettings, setLeaveSettings] = useState<ILeaveSetting[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string>("");

    const handleGetLeaveSettings = async () => {
        try {
            const response = await LeaveService.getLeaveSettings();
            if (response?.status) {
                // Ensure data falls back to empty array if undefined
                const settings = response?.data?.leaveSettings || [];
                setLeaveSettings(settings);
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    const handleDeleteLeaveSetting = async () => {
        try {
            const response = await LeaveService.deleteLeaveSetting(deleteId);
            if (response?.status) {
                showToastNotification("success", response?.message)
                toggleDeleteDialog("")
                handleGetLeaveSettings();
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
    }

    useEffect(() => {
        handleGetLeaveSettings();
    }, []);

    const toggleDeleteDialog = (id: string = "") => {
        setDeleteId(id ?? "");
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const columns: TableColumn<ILeaveSetting>[] = [
        {
            key: 'srNo',
            header: 'Sr No',
            render: (_item: ILeaveSetting, index: number) => (
                <span className="text-muted-foreground font-medium"># {index + 1}</span>
            )
        },
        {
            key: 'type',
            header: 'Leave Type',
            render: (item: ILeaveSetting) => (
                <span className="font-semibold capitalize">{item?.type}</span>
            )
        },
        {
            key: 'applicableFrom',
            header: 'Applicable From',
            render: (item: ILeaveSetting) => (
                <span className="font-medium">
                    {item?.applicableFrom ? moment(item.applicableFrom).format("DD-MM-YYYY") : "—"}
                </span>
            )
        },
        {
            key: 'totalLeavePerMonth',
            header: 'Leave/Month',
            render: (item: ILeaveSetting) => (
                <span className="font-medium">{item?.totalLeavePerMonth}</span>
            )
        },
        {
            key: 'totalLeavePerYear',
            header: 'Leave/Year',
            render: (item: ILeaveSetting) => (
                <span className="font-medium">{item?.totalLeavePerYear}</span>
            )
        },
        {
            key: 'monthlyCarryForward',
            header: 'Monthly Carry Forward',
            render: (item: ILeaveSetting) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.monthlyCarryForward ? 'text-blue-600 border-blue-600 bg-blue-50' : 'text-slate-500 border-slate-300 bg-slate-50'}`}>
                    {item?.monthlyCarryForward ? 'Yes' : 'No'}
                </span>
            )
        },
        {
            key: 'yearCarryForward',
            header: 'Yearly Carry Forward',
            render: (item: ILeaveSetting) => (
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.yearCarryForward ? 'text-blue-600 border-blue-600 bg-blue-50' : 'text-slate-500 border-slate-300 bg-slate-50'}`}>
                    {item?.yearCarryForward ? 'Yes' : 'No'}
                </span>
            )
        },
        // {
        //     key: 'status',
        //     header: 'Status',
        //     render: (item: ILeaveSetting) => (
        //         <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
        //             {item?.isActive ? 'Active' : 'Inactive'}
        //         </span>
        //     )
        // },
        {
            key: 'actions',
            header: 'Actions',
            render: (item: ILeaveSetting) => (
                <div className="flex gap-2">
                    <Button
                        size={'icon'}
                        className="bg-primary/10 text-primary hover:bg-primary/20"
                        title="Edit Leave Setting"
                        onClick={() => navigate(`${RoutesLink.editLeaveSetting}/${item.id}`)}
                    >
                        <Pencil size={18} />
                    </Button>
                    <Button
                        size={'icon'}
                        variant={'destructive'}
                        className="bg-destructive/10 text-destructive hover:bg-destructive/20"
                        title="Delete Leave Setting"
                        onClick={() => toggleDeleteDialog(item.id)}
                    >
                        <Trash size={18} />
                    </Button>
                </div>
            )
        }
    ]

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Leave Settings"
                    buttonText="Add Leave Setting"
                    buttonIcon={<Plus size={18} />}
                    onClick={() => navigate(RoutesLink.addLeaveSetting)}
                />

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <CommonTable
                        columns={columns}
                        data={leaveSettings}
                    />
                </div>

                <CommonModal
                    show={deleteDialogOpen}
                    onClose={() => toggleDeleteDialog("")}
                    title=""
                    className="max-w-md rounded-2xl p-0 overflow-hidden"
                >
                    <div className="p-6">
                        <div className="flex flex-col items-center text-center">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                                <TriangleAlert className="text-red-600 w-8 h-8" />
                            </div>
                            <h2 className="mt-5 text-2xl font-bold text-gray-900">Delete Leave Setting?</h2>
                            <p className="mt-3 text-sm leading-6 text-gray-500">
                                This action cannot be undone. Once deleted, the leave setting will be permanently revoked.
                            </p>
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Button variant="outline" className="flex-1" onClick={() => toggleDeleteDialog()}>
                                Cancel
                            </Button>
                            <Button variant="destructive" className="flex-1" onClick={handleDeleteLeaveSetting}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </CommonModal>
            </div>
        </Layout>
    )
}

export default LeaveSettings